'use client';

// Global token getter - will be set by the app
let getTokenFn: (() => string | null) | null = null;

export function setAuthTokenGetter(fn: () => string | null) {
  getTokenFn = fn;
}

/**
 * Optional host-supplied provider of extra request headers attached to every
 * Meridian chat send (DefaultChatTransport + the file-upload fetch path).
 *
 * Used by the Chrome extension to inject `X-Browsing-Context` when the user
 * has opted in. Must be synchronous; cache any async source upstream.
 */
let getExtraHeadersFn: (() => Record<string, string>) | null = null;

export function setExtraRequestHeadersProvider(
  fn: (() => Record<string, string>) | null
) {
  getExtraHeadersFn = fn;
}

export function getExtraRequestHeaders(): Record<string, string> {
  if (!getExtraHeadersFn) return {};
  try {
    return getExtraHeadersFn() ?? {};
  } catch (error) {
    console.warn('[getExtraRequestHeaders] provider threw, ignoring:', error);
    return {};
  }
}

export interface AuthenticatedFetchOptions extends RequestInit {
  retries?: number;
  retryDelay?: number;
}

export interface AuthenticatedFetchError extends Error {
  status?: number;
  code?: string;
}

/**
 * Get auth token from the registered token getter (sync - used by all authenticated requests)
 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    if (getTokenFn) {
      const token = getTokenFn();
      if (!token) {
        console.warn('[getAuthToken] Token getter returned null/empty');
      }
      return token;
    }
    
    console.warn('[getAuthToken] No token getter registered, falling back to localStorage');
    
    // Fallback: try to get from Supabase session storage
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn('[getAuthToken] No NEXT_PUBLIC_SUPABASE_URL');
      return null;
    }
    
    const sessionKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
    const sessionData = localStorage.getItem(sessionKey);
    
    if (!sessionData) {
      console.warn('[getAuthToken] No session data in localStorage');
      return null;
    }
    
    const session = JSON.parse(sessionData);
    return session?.session?.access_token || null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Make an authenticated API request with JWT token from Supabase
 * Automatically includes Authorization header with Bearer token
 * Supports retry logic for network failures
 */
export async function authenticatedFetch<T = unknown>(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<T> {
  const { retries = 3, retryDelay = 1000, ...fetchOptions } = options;

  let lastError: AuthenticatedFetchError | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Get JWT token from auth provider
      const token = getAuthToken();

      if (!token) {
        console.warn('[authenticatedFetch] NO_AUTH_TOKEN for URL:', url, '- attempting with session cookies only');
        // Don't throw - session cookies may be sufficient for internal APIs
        // Just proceed without the Authorization header
      }

      // Prepare headers with authorization
      const headers = new Headers(fetchOptions.headers || {});
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      if (!headers.has('Content-Type') && fetchOptions.body) {
        headers.set('Content-Type', 'application/json');
      }

      console.log('[authenticatedFetch] Making request to:', url, 'with token:', token ? token.substring(0, 20) + '...' : 'none (using session cookies)');

      // Make the request
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include',
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorData: any;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: response.statusText };
        }

        const error = new Error(
          errorData.message || errorData.error?.message || `API request failed: ${response.statusText}`
        ) as AuthenticatedFetchError;
        error.status = response.status;
        error.code = errorData.error?.code;

        // Don't retry on 401/403 (auth errors)
        if (response.status === 401 || response.status === 403) {
          throw error;
        }

        // Retry on 5xx errors or network issues
        if (response.status >= 500 && attempt < retries) {
          lastError = error;
          await new Promise((resolve) => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
          continue;
        }

        throw error;
      }

      // Success - parse and return response
      const result = (await response.json()) as T;
      return result;
    } catch (error) {
      lastError = error as AuthenticatedFetchError;

      // Don't retry on auth errors (but allow session-cookie-only requests)
      if (error instanceof Error && 'code' in error && error.code === 'NO_AUTH_TOKEN') {
        throw error;
      }

      // Don't retry on 401/403
      if (error instanceof Error && 'status' in error && (error.status === 401 || error.status === 403)) {
        throw error;
      }

      // Retry on network errors or 5xx
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
        continue;
      }

      throw error;
    }
  }

  // All retries exhausted
  throw lastError || new Error('Failed to make authenticated request');
}

/**
 * GET request with authentication
 */
export async function authenticatedGet<T = unknown>(url: string, options?: AuthenticatedFetchOptions): Promise<T> {
  return authenticatedFetch<T>(url, { ...options, method: 'GET' });
}

/**
 * POST request with authentication
 */
export async function authenticatedPost<T = unknown>(
  url: string,
  body?: unknown,
  options?: AuthenticatedFetchOptions
): Promise<T> {
  return authenticatedFetch<T>(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request with authentication
 */
export async function authenticatedPut<T = unknown>(
  url: string,
  body?: unknown,
  options?: AuthenticatedFetchOptions
): Promise<T> {
  return authenticatedFetch<T>(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request with authentication
 */
export async function authenticatedPatch<T = unknown>(
  url: string,
  body?: unknown,
  options?: AuthenticatedFetchOptions
): Promise<T> {
  return authenticatedFetch<T>(url, {
    ...options,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request with authentication
 */
export async function authenticatedDelete<T = unknown>(url: string, options?: AuthenticatedFetchOptions): Promise<T> {
  return authenticatedFetch<T>(url, { ...options, method: 'DELETE' });
}

/**
 * Authenticated fetch that returns the raw Response (for streaming).
 * Caller is responsible for reading response.body (e.g. getReader()).
 * Uses the same Supabase token getter as authenticatedPost/authenticatedFetch.
 */
export async function authenticatedFetchResponse(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    const error = new Error('No valid auth token found') as AuthenticatedFetchError;
    error.code = 'NO_AUTH_TOKEN';
    throw error;
  }
  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
}
















